/* eslint-disable no-console */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useEncryption } from "./encryptionHelper";
import type {
  AcknowledgementMessage,
  DataMessage,
  Message,
  HookReturn,
  MessageSender,
  Interval,
} from "./types";
import { MessageOrigin } from "./types";
import { MessageStatus, MessageType } from "./types";
import { messageRepo } from "./repo";

export const useMessengerHook: () => HookReturn = () => {
  const retryInterval = 1500;
  const retryTimeout = 7500;
  const autoRedirect = true;

  const [isConnectionEstablished, setIsConnectionEstablished] = useState(false);

  const { storeMessage, resolveMessage, getMessage, currentIndex, clearStore } = messageRepo();
  const sender = useRef<MessageSender>();

  const navigate = useNavigate();

  const [messageReceivedSubscribers, setMessageReceivedSubscribers] = useState<
    ((message: Message) => void | string)[]
  >([]);
  const [dataReceivedSubscribers, setDataReceivedSubscribers] = useState<
    ((dataKey: string, dataValue: string) => void | string)[]
  >([]);
  const [redirectRequestedSubscribers, setRedirectRequestedSubscribers] = useState<
    ((url: string) => void | string)[]
  >([]);

  const subscribeToMessageReceived = (callback: (message: Message) => void | string) => {
    setMessageReceivedSubscribers([...messageReceivedSubscribers, callback]);
  };
  const subscribeToDataReceived = (
    callback: (dataKey: string, dataValue: string) => void | string,
  ) => {
    setDataReceivedSubscribers([...dataReceivedSubscribers, callback]);
  };
  const subscribeToRedirectRequested = (callback: (url: string) => void | string) => {
    setRedirectRequestedSubscribers([...redirectRequestedSubscribers, callback]);
  };

  const [intervals] = useState<Interval[]>([]);

  const { encrypt, decrypt } = useEncryption();

  const sendAcknowledgement = (id: number, error?: string) => {
    const ack: AcknowledgementMessage = {
      type: MessageType.Ack,
      id,
      state: !error ? MessageStatus.Success : MessageStatus.Error,
      error,
    };

    sender.current?.source.postMessage(encrypt(ack), {
      targetOrigin: sender.current.origin,
    });
  };

  const sendMessage = (message: Message) => {
    message.state = MessageStatus.Sending;
    storeMessage(MessageOrigin.Sent, message);

    const send = () =>
      sender.current?.source.postMessage(encrypt(message), {
        targetOrigin: sender.current.origin,
      });

    send();

    if (intervals[message.id]) return;

    intervals[message.id] = {
      intervalId: window.setInterval(() => {
        if (!intervals[message.id].wasStopped) send();
      }, retryInterval),
      message: message,
      wasStopped: false,
    };
    window.setTimeout(() => {
      if (intervals[message.id]) {
        clearInterval(intervals[message.id].intervalId);
        intervals[message.id].wasStopped = true;
        message.state = MessageStatus.Error;
        storeMessage(MessageOrigin.Sent, message);
      }
    }, retryTimeout);
  };

  const sendDataMessage = (key: string, value: string) => {
    const message: DataMessage = {
      id: 0 + currentIndex,
      type: MessageType.Data,
      state: MessageStatus.Pending,
      dataKey: key,
      dataValue: value,
    };

    storeMessage(MessageOrigin.Sent, message);
    sendMessage(message);
  };

  const handleMessageEvent = (event: MessageEvent<string>) => {
    const message = decrypt(event.data);

    if (message.type == MessageType.Ack) {
      if (getMessage(message.id)?.state != MessageStatus.Sending) {
        console.warn([
          "Received a duplicate message",
          { received: message, stored: getMessage(message.id) },
        ]);

        return;
      }

      messageReceivedSubscribers.forEach((cb) => cb({ ...message }));

      if (intervals[message.id]) {
        clearInterval(intervals[message.id].intervalId);
        intervals[message.id].wasStopped = true;
      }

      resolveMessage(message);
    } else {
      let error: string | undefined = undefined;

      if (message.type === MessageType.Init) {
        if (isConnectionEstablished)
          error = "Received an init message but the connection was already established";
        else if (event.source) {
          if (message.id === 0) clearStore();
          storeMessage(MessageOrigin.Received, message);
          sender.current = { source: event.source, origin: event.origin };
          setIsConnectionEstablished(true);
        }
      } else {
        if (getMessage(message.id)) {
          console.warn([
            "Received a duplicate message",
            { received: message, stored: getMessage(message.id) },
          ]);

          return;
        }

        messageReceivedSubscribers.forEach((cb) => cb({ ...message }));

        if (!error)
          switch (message.type) {
            case MessageType.Data:
              storeMessage(MessageOrigin.Received, message);

              dataReceivedSubscribers.forEach((cb) => cb(message.dataKey, message.dataValue));

              break;
            case MessageType.Redir:
              storeMessage(MessageOrigin.Received, message);

              redirectRequestedSubscribers.forEach((cb) => cb(message.url));

              break;
          }
      }

      if (error) console.error(error);
      message.state = error ? MessageStatus.Error : MessageStatus.Success;

      storeMessage(MessageOrigin.Received, message);
      sendAcknowledgement(message.id, error as string | undefined);

      if (autoRedirect && message.type === MessageType.Redir) navigate(message.url);
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessageEvent);

    return () => {
      window.removeEventListener("message", handleMessageEvent);
    };
  });

  return {
    isConnectionEstablished,
    sendDataMessage,
    getMessage,
    subscribeToMessageReceived,
    subscribeToDataReceived,
    subscribeToRedirectRequested,
  };
};
